import { NextComponentType, NextPageContext } from 'next';
import * as React from 'react';
import ProtectedPage from '../../components/merchants/products/MerchantProductProtectedPage';
import Error, { Props } from '../../pages/_error';
import implUCMerchantRepository from '../../infrastructure/repositories/UCMerchantRepository';

export interface QueryProps {
  session: string;
  publicId: string;
  productId: string;
}

type State = StateInitial | StatePurchaseAvailable | StateError;

interface StateInitial {
  type: 'initial';
}

interface StatePurchaseAvailable {
  type: 'available';
  success: boolean | undefined;
}

interface StateError {
  type: 'error';
  statusCode: Props['statusCode'];
}

type Component = typeof React.Component | NextComponentType;

export default function<WrappedProps>(WrappedComponent: Component) {
  const isNext = (c: Component): c is NextComponentType =>
    c.hasOwnProperty('getInitialProps');

  type PurchaseAvailableProps = WrappedProps & QueryProps;

  return class PurchaseAvailable extends React.Component<
    PurchaseAvailableProps,
    State
  > {
    constructor(props: PurchaseAvailableProps) {
      super(props);
      this.state = { type: 'initial' };
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    public static async getInitialProps(ctx: NextPageContext & QueryProps) {
      if (isNext(WrappedComponent)) {
        const pageProps = WrappedComponent.getInitialProps
          ? await WrappedComponent.getInitialProps(ctx)
          : {};
        return { ...pageProps, query: ctx.query };
      }

      return { query: ctx.query };
    }

    public async componentDidMount() {
      const { session, publicId, productId } = this.props;

      try {
        const result = await implUCMerchantRepository(session).Product(
          publicId,
          parseInt(productId, 10),
        );

        this.setState({
          type: 'available',
          success: !result.isPasswordProtected,
        });
      } catch (e) {
        this.setState({ type: 'error', statusCode: e.response.status });
      }
    }

    public handleSubmit(result: boolean) {
      this.setState({ type: 'available', success: result });
    }

    public render() {
      const { session, productId } = this.props;
      if (this.state.type === 'initial') {
        return null;
      }
      if (this.state.type === 'error') {
        return <Error statusCode={this.state.statusCode} />;
      }
      if (!this.state.success) {
        return (
          <ProtectedPage
            session={session}
            productPublicId={parseInt(productId, 10)}
            onSubmit={this.handleSubmit}
          />
        );
      }
      return <WrappedComponent {...this.props} />;
    }
  };
}
